import type { MessageDescriptor } from '@atlassian/jira-intl';
import type { JsmTemplateGroup } from './JsmTemplateGroup.types.tsx';

export const JSM_CONTEXTUAL_ROVO_EXPERIMENT_KEY = 'JSMGROWTH-524';
export const JSM_CONTEXTUAL_ROVO_KILLSWITCH_KEY = 'grw-524_jsm_jpd_rovo_sidekick_killswitch';
export const JSM_CONTEXTUAL_ROVO_PROMPT_SOURCE = 'jsm-contextual-suggestions';
export const JSM_CONTEXTUAL_ROVO_PROMPT_SURFACE = 'rovoQuickActions';
export const JSM_CONTEXTUAL_ROVO_PROMPT_WINDOW_DAYS = 60;

export type JsmContextualPageContext =
	| 'get_started'
	| 'queues'
	| 'request_types'
	| 'forms'
	| 'user_management'
	| 'automations'
	| 'knowledge_base';

export type JsmContextualPromptCategory =
	| 'orientation'
	| 'queues'
	| 'request_types'
	| 'forms'
	| 'user_management'
	| 'automations'
	| 'knowledge_base';

export type JsmSuggestionDefinition = {
	id: string;
	category: JsmContextualPromptCategory;
	starterPrompt: MessageDescriptor;
	staticPromptPrefix?: string;
	skillRegistryTags?: string[];
};

export type DaysSinceBucket = 'D0' | 'D1_2' | 'D3_6' | 'D7_plus' | 'unknown';

export type JsmContextualPromptAnalyticsMetadata = {
	promptId: string;
	promptCategory: JsmContextualPromptCategory;
	promptSetId: string;
	pageContext: JsmContextualPageContext;
	routeName: string;
};

/**
 * The template bucket that selected the prompt set, for analytics.
 *
 * Deliberately wider than {@link JsmTemplateGroup}: that union drives the
 * `Record<JsmTemplateGroup, …>` prompt maps, so widening it would force an `unknown` entry into
 * every one. `unknown` means the project template was not known when the metadata was built (the
 * project property resolves asynchronously) — reporting `General` there would silently mis-bucket
 * IT and HR projects.
 */
export type JsmTemplateGroupForAnalytics = JsmTemplateGroup | 'unknown';

export type JsmContextualPromptSetAnalyticsMetadata = {
	experimentKey: 'JSMGROWTH-524';
	killswitchKey: 'grw-524_jsm_jpd_rovo_sidekick_killswitch';
	routeName: string;
	/**
	 * The router route *pattern* (for example `/jira/servicedesk/projects/:projectKey/queues`),
	 * never the concrete URL — the resolved path contains project keys and would inflate cardinality.
	 */
	routePath?: string;
	/** Raw project template key. Omitted when the project template is not yet known. */
	projectTemplateId?: string;
	templateGroup: JsmTemplateGroupForAnalytics;
	pageContext: JsmContextualPageContext;
	promptSetId: string;
	promptIds: string[];
	prompts: string[];
	promptCategories: JsmContextualPromptCategory[];
	promptCount: number;
	promptMetadata: JsmContextualPromptAnalyticsMetadata[];
	promptSource: 'jsm-contextual-suggestions';
	promptSurface: 'rovoQuickActions';
	promptWindowDays: 60;
	viewedAt?: number;
};

export type JsmContextualPromptEventMetadata = JsmContextualPromptSetAnalyticsMetadata & {
	daysSinceSignupBucket?: DaysSinceBucket;
	daysSinceJsmActivationBucket?: DaysSinceBucket;
};

export type JsmContextualPromptSetViewedEventMetadata = JsmContextualPromptEventMetadata & {
	mode: 'mini-modal' | 'sidebar';
	productHeader: 'jsm';
	isWithinPromptWindow: true;
};

/**
 * Flat attributes spread onto `emptyStateSuggestions` click for a contextual
 * action. Must not include prompt text or user content.
 */
export type JsmContextualPromptClickAttributes = {
	experimentKey: typeof JSM_CONTEXTUAL_ROVO_EXPERIMENT_KEY;
	killswitchKey: typeof JSM_CONTEXTUAL_ROVO_KILLSWITCH_KEY;
	routeName: string;
	routePath?: string;
	projectTemplateId?: string;
	templateGroup: JsmTemplateGroupForAnalytics;
	pageContext: JsmContextualPageContext;
	promptId: string;
	promptCategory: JsmContextualPromptCategory;
	promptSetId: string;
	promptSource: typeof JSM_CONTEXTUAL_ROVO_PROMPT_SOURCE;
	promptSurface: typeof JSM_CONTEXTUAL_ROVO_PROMPT_SURFACE;
	promptWindowDays: typeof JSM_CONTEXTUAL_ROVO_PROMPT_WINDOW_DAYS;
	isWithinPromptWindow: true;
};
